import { appendAppLog } from '../../platform/appLog';
import { alsBase64, baueUebergabeTeile, datenbestand, IST_VIDEO, sammleBelege } from '../../platform/beleguebergabe';
import { MultipartUpload } from '../../platform/multipart';
import type { Store } from '../../persistence/port';
import { basicKopfzeile } from './client';

/**
 * Der Diagnose-Upload auf `UPLOAD_LOGS`: **ein rohes ZIP** an
 * `POST {host}/rest/upload/logs/{deviceID}.zip` (`docs/classic-rest-api.md` §8), wie es die
 * alte Classic-App schickte. Bis zum 2026-10-09 gingen stattdessen die Tagesprotokolle einzeln
 * über den Datei-Upload — der Server legte sie dort ab, wo niemand Diagnosen sucht.
 *
 * **Inhalt:** alles unter `logs/` (Tagesprotokolle, Aufnahmen, Abzüge, Beglaubigungen) und der
 * Datenbestand als `datenbank.json` — die alte App legte ihre Datenbankdatei bei.
 *
 * ⚠️ **Ohne Videos.** Das ZIP geht als Base64 über die Brücke zum nativen Teil und liegt dabei
 * mehrfach im Speicher; eine halbe Stunde Video sprengte das. Videos kommen ohnehin über den
 * Datei-Upload an.
 *
 * ⚠️ **Ohne `ClientSettingValue`.** Dort stehen Passwörter und Serverzugang; ein Diagnose-ZIP
 * liegt auf dem Server, wo es viele lesen können.
 */
export const DIAGNOSE_PFAD = '/rest/upload/logs/';

export interface DiagnoseDeps {
  zugang: { baseUrl: string; login: string; password: string };
  deviceID: string;
  store?: Store;
  log?: (zeile: string) => Promise<void>;
  /** Einschiebbar für Tests: das fertige ZIP als Base64. */
  packe?: () => Promise<string>;
  /** Einschiebbar für Tests: der rohe `POST`. */
  sende?: (url: string, kopf: Record<string, string>, base64: string) => Promise<{ ok: boolean; status: number; text: string }>;
}

export function diagnoseUrl(baseUrl: string, deviceID: string): string {
  return baseUrl.replace(/\/+$/, '') + DIAGNOSE_PFAD + encodeURIComponent(deviceID) + '.zip';
}

export async function ladeDiagnoseHoch(deps: DiagnoseDeps): Promise<boolean> {
  const log = deps.log ?? appendAppLog;
  const packe =
    deps.packe ??
    (async () => {
      const zusatz = deps.store ? [{ name: 'datenbank.json', daten: await datenbestand(deps.store) }] : [];
      const { stuecke } = await sammleBelege(baueUebergabeTeile(), { zusatz, auslassen: (p) => IST_VIDEO.test(p) });
      return alsBase64(stuecke);
    });
  const sende =
    deps.sende ??
    ((url, kopf, base64) =>
      MultipartUpload.uploadRoh({ url, headers: kopf, contentType: 'application/zip', base64 }));

  try {
    const base64 = await packe();
    const antwort = await sende(
      diagnoseUrl(deps.zugang.baseUrl, deps.deviceID),
      { Authorization: basicKopfzeile(deps.zugang.login, deps.zugang.password) },
      base64,
    );
    const abschlaegig = antwort.text.replace(/\s+/g, '').includes('"success":false');
    if (!antwort.ok || abschlaegig) {
      await log('Diagnose-Upload (Classic) abgelehnt: Status ' + String(antwort.status));
      return false;
    }
    await log('Diagnose-Upload (Classic): ' + String(Math.round((base64.length * 3) / 4)) + ' Bytes übertragen.');
    return true;
  } catch (fehler) {
    // Wie bei der alten App: kein eigener Neuversuch — der Prüfungsleiter fordert erneut an.
    await log('Diagnose-Upload (Classic) gescheitert: ' + String(fehler));
    return false;
  }
}
